import React, { useState } from "react";
import { useAnimation } from "./AnimationContext";
import { Plus, Search, FolderOpen, Type, Image } from "lucide-react";

export default function AnimationsSidebar() {
  const {
    assets,
    uploadAsset,
    addNewElementFromAsset,
    addTextElement,
    canvasBackground,
    setCanvasBackgroundFile,
    clearCanvasBackground,
    canvasBackgroundColor,
    setCanvasBackgroundColor,
  } = useAnimation();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAssets = assets.filter((a) =>
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) uploadAsset(file);
    e.target.value = "";
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) setCanvasBackgroundFile(file);
    e.target.value = "";
  };

  const handleDragStart = (e, asset) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(asset));
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border-r border-gray-200">
        <div className="flex-1 flex flex-col overflow-hidden p-3">
          {/* Search + Upload */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purpleColor"
              />
            </div>
            <label className="flex items-center justify-center px-2.5 py-1.5 bg-purpleColor hover:bg-[#7a1b51] text-white rounded-lg text-xs font-semibold cursor-pointer transition">
              <Plus className="w-3.5 h-3.5" />
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>

          {/* Canvas Background */}
          <div className="mb-3 rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <Image className="w-3.5 h-3.5" />
                Canvas Background
              </div>
              <div className="flex gap-1">
                <label className="text-[10px] bg-purpleColor text-white px-2 py-0.5 rounded cursor-pointer hover:bg-[#7a1b51] transition font-semibold">
                  {canvasBackground ? "Change" : "Set"}
                  <input type="file" accept="image/*" onChange={handleBackgroundUpload} className="hidden" />
                </label>
                {canvasBackground && (
                  <button
                    onClick={() => clearCanvasBackground()}
                    className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded hover:bg-red-100 hover:text-red-600 transition font-semibold"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div
              className="h-16 flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: canvasBackgroundColor || "#1a1a2e" }}
            >
              {canvasBackground ? (
                <img src={canvasBackground} alt="bg" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[10px] text-white/40 font-medium">No image set</span>
              )}
            </div>
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50">
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Solid Color</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={canvasBackgroundColor || "#1a1a2e"}
                  onChange={(e) => setCanvasBackgroundColor(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={canvasBackgroundColor || "#1a1a2e"}
                  onChange={(e) => setCanvasBackgroundColor(e.target.value)}
                  className="w-20 border border-gray-200 rounded px-1.5 py-1 text-[10px] focus:border-purpleColor focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Add Text Button */}
          <button
            onClick={addTextElement}
            className="mb-3 w-full flex items-center justify-center gap-2 py-2 border border-dashed border-purpleColor text-purpleColor hover:bg-purpleColor/5 rounded-lg text-xs font-semibold transition"
          >
            <Type className="w-3.5 h-3.5" />
            Add Text Element
          </button>

          {/* Assets Grid */}
          <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {filteredAssets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 px-4">
                <FolderOpen className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-xs">No assets yet.</p>
                <p className="text-[10px] mt-1">Click <span className="font-bold">+</span> to upload an image.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5 pb-2">
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, asset)}
                    onClick={() => addNewElementFromAsset(asset)}
                    className="group border border-gray-200 hover:border-purpleColor rounded-xl p-2 bg-slate-50 flex flex-col items-center justify-center cursor-grab active:cursor-grabbing transition"
                  >
                    <div className="w-full h-20 rounded-lg overflow-hidden bg-white flex items-center justify-center mb-1.5">
                      <img
                        src={asset.thumbnail}
                        alt={asset.name}
                        className="object-contain h-full w-full"
                      />
                    </div>
                    <span className="text-[10px] text-gray-600 truncate w-full text-center font-medium">
                      {asset.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
    </div>
  );
}
